import {
  BaseSyntheticEvent,
  CSSProperties,
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import classNames from 'classnames';
import TooltipedCrown from '@picsart/rc/old/TooltipedCrown';
import { useTranslator } from '@picsart/rc/services/localization';
import InputRange from '@picsart/rc/InputRange';
import Thumbnail, { Footer, Title } from '@picsart/rc/Thumbnail';
import { useLocation } from 'react-router-dom';
import { ImageHolderSize, ImageHoldertype } from '@picsart/rc/ImageHolder';

import { DEEPLINKS } from 'configs/constants';
import Text from 'components/common/Text';

import {
  cutoutSettingsData,
  cutoutSettingsDataNew,
  EraserCutoutEnum,
  eraserSettingsData,
  EraserSettingsEnum,
  EraserToolsEnum,
  IEraserSetting,
  ERASER_SIZE_MIN_VALUE,
} from './config';
import { calculatePercent } from './utils';
import { useSettingsStyle } from './styles';

export type SettingDataType = {
  [key: string]: number;
};

interface IEraserSettingsProps {
  settings: SettingDataType;
  onSettingsChange: Dispatch<SetStateAction<SettingDataType>>;
  activeTool: EraserToolsEnum | null;
  onCutout: (type: 'object' | 'person', isDeeplink?: boolean) => void;
  appliedRemoverTool?: null | string;
  loading: boolean;
  isDarkMode: boolean;
}

const EraserSettings = ({
  settings,
  onSettingsChange,
  activeTool,
  onCutout,
  appliedRemoverTool,
  loading,
  isDarkMode,
}: IEraserSettingsProps) => {
  const classes = useSettingsStyle({
    isDarkMode,
  });
  const translate = useTranslator();
  const location = useLocation();
  const autoAppliedCutout = useRef(false);

  const handleCutout = useCallback((type, isDeeplink?) => {
    if (loading || type === appliedRemoverTool) {
      return;
    }

    onCutout(type, isDeeplink);
  }, [appliedRemoverTool, onCutout, loading]);

  const handleCutoutType = useCallback((e: BaseSyntheticEvent) => {
    const { type } = e.currentTarget.dataset;
    handleCutout(type);
  }, [handleCutout]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const eraserType = searchParams.get(DEEPLINKS.eraserType) as EraserCutoutEnum || null;

    if ([EraserCutoutEnum.Object, EraserCutoutEnum.Person].includes(eraserType) && !autoAppliedCutout.current && !loading) {
      handleCutout(eraserType, true);
      autoAppliedCutout.current = true;
    }
  }, [handleCutout, loading, location.search]);

  const handleBlur = useCallback((e: BaseSyntheticEvent) => {
    const { value, dataset: { type } } = e.target;

    if (!value) {
      onSettingsChange(prev => ({
        ...prev,
        [type]: type === EraserSettingsEnum.SIZE ? 1 : 0,
      }));
    }
  }, [onSettingsChange]);

  const handleChangeValue = useCallback((type: EraserSettingsEnum, currentValue: number | string, maxValue = 100, minValue = 0) => {
    let tempValue = currentValue;
    if (currentValue !== '') {
      tempValue = Number(currentValue);
    }

    if (Number.isNaN(tempValue)) {
      return;
    }

    if (type === EraserSettingsEnum.SIZE && parseInt(tempValue.toString(), 10) === 0) {
      tempValue = ERASER_SIZE_MIN_VALUE;
    }

    let val = tempValue > maxValue ? maxValue : tempValue;
    val = val < minValue ? (type === EraserSettingsEnum.SIZE ? ERASER_SIZE_MIN_VALUE : minValue) : val;
    if (!tempValue && tempValue !== 0) {
      val = '';
    }

    onSettingsChange(prev => ({
      ...prev,
      [type]: val,
    }));
  }, [onSettingsChange]);

  const handleChangeSettings = useCallback(e => {
    const { max: maxValue, min: minValue, value: currentValue, dataset: { type } } = e.target;

    handleChangeValue(type, currentValue, maxValue, minValue);
  }, [handleChangeValue]);

  const disableDotTyping = useCallback(e => {
    if (['-', '.'].includes(e.key)) {
      e.preventDefault();
    }
  }, []);

  const currentSetting = useCallback(({
    min, max, type, name,
  }: IEraserSetting) => {
    const value = settings[type];
    const percent = calculatePercent(value, min || 0, max || 0);

    const rangeProps = {
      value,
      'data-type': type,
      'data-test': name,
      onChange: handleChangeSettings,
    };

    const inputProps = {
      size: 28,
      type: 'text',
      value,
      'data-type': type,
      'data-test': `${name}-input`,
      onChange: handleChangeSettings,
      onBlur: handleBlur,
      onKeyDown: disableDotTyping,
    };

    return (
      <div key={type} className={classes.inputRangContainer}>
        {!isDarkMode ? (
          <div className={classes.settingName}>
            {translate(name)}
          </div>
        ) : (
          <Text size="small" weight="medium" color="secondary" className={classes.settingName}>
            {translate(name)}
          </Text>
        )}

        <div className={classes.rangeWrapper}>
          {!isDarkMode ? (
            <>
              <div className={classes.rangeWrap} style={{ '--progressValue': `${percent}%` } as CSSProperties}>
                <input
                  className={classes.inputRange}
                  type="range"
                  min={min}
                  max={max}
                  value={value || 0}
                  onChange={e => handleChangeValue(type, Number(e.target.value))}
                />
              </div>
              <input
                className={classes.settingValue}
                value={value}
                onChange={e => handleChangeValue(type, e.target.value)}
                onKeyDown={disableDotTyping}
                min={min}
                max={max}
                onBlur={handleBlur}
                data-type={type}
              />
            </>
          ) : (
            <InputRange
              max={max}
              min={min}
              step={1}
              rangeProps={{ ...rangeProps }}
              inputProps={{ ...inputProps }}
            />
          )}
        </div>
      </div>
    );
  }, [classes, handleChangeValue, settings, disableDotTyping, translate, handleBlur, isDarkMode, handleChangeSettings]);

  const cutOutContent = useMemo(() => {
    if (isDarkMode) {
      return cutoutSettingsDataNew.map(({ url, type, title }) => (
        <div
          className={classes.cutoutImageContainer}
          key={type}
        >
          <Thumbnail
            imageUrl={url}
            width={150}
            height={150}
            onClick={handleCutoutType}
            data-type={type}
            isPremium={type === EraserCutoutEnum.Object}
            premiumTooltip={translate('This is a premium feature')}
            selected={appliedRemoverTool === type}
            size={ImageHolderSize.Large}
            type={ImageHoldertype.Collage}
            pinableChildren
            enablePlaceholder
          >
            <Footer pin><Title title={translate(title)} /></Footer>
          </Thumbnail>
        </div>
      ));
    }

    return cutoutSettingsData.map(({ url, type, title }) => (
      <div
        className={classNames(classes.currentImgContainer, {
          [classes.appliedRemoverTool]: appliedRemoverTool === type,
          [classes.applyHover]: appliedRemoverTool !== type,
          [classes.disabledState]: loading,
        })}
        onClick={() => handleCutout(type)}
        key={type}
      >
        {type === EraserCutoutEnum.Object && (
          <TooltipedCrown
            message={translate('This is a premium feature')}
            className={classes.crown}
            size="medium"
          />
        )}
        <img
          src={url}
          alt={translate('img')}
          className={classes.image}
        />
        <p className={classes.cutoutTitle}>{translate(title)}</p>
      </div>
    ));
  }, [appliedRemoverTool, loading, isDarkMode, translate, handleCutout, handleCutoutType, classes]);

  return (
    activeTool === EraserToolsEnum.CUT_OUT ? (
      <div className={classes.cutoutSettingsContainer}>
        {cutOutContent}
      </div>
    ) : (
      <div className={classes.settingsContainer}>
        {eraserSettingsData.map(el => currentSetting(el))}
      </div>
    )
  );
};

export default memo(EraserSettings);
